import {
  FindingsOrder,
  FindingsOrderField,
  FindingType,
  OrderDirection,
  useGetRelatedAdvisoryFindingsQuery,
} from "~/operations";
import { useFindingOnAsset } from "../../hooks/useFindingOnAsset";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { SectionHeading } from "~/components/DetailsPage/components";
import { Fragment } from "react/jsx-runtime";
import { ScrollToTop } from "~/lib/scroll-to-top";
import { FindingsTable } from "~/pages/inventory/components/Findings/FindingsTable/FindingsTable";
import { Loading } from "~/components/loading";
import { Box } from "@mui/material";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { usePaginate } from "~/components/pagination/usePaginate";
import { EmptySection } from "~/components/vulnerabilities";

interface Props {
  finding: ReturnType<typeof useFindingOnAsset>["finding"];
}

export function CveRelatedAdvisoryFindings({ finding }: Props) {
  const paginate = usePaginate();
  const baseUrl = paginate.resetCursorUrl(location.href);
  const sort = useSort<FindingsOrder>({
    baseUrl,
    defaultSort: {
      field: FindingsOrderField.RiskScore,
      direction: OrderDirection.Desc,
    },
    validFields: [FindingsOrderField.RiskScore],
  });

  const { data, loading } = useGetRelatedAdvisoryFindingsQuery({
    variables: {
      scopeMrn: finding?.asset?.mrn || "",
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        vulnerabilityMrn: finding?.mrn,
        types: [FindingType.Advisory],
      },
    },
  });

  const findingsUnion =
    data?.findings?.__typename === "FindingsConnection"
      ? data.findings
      : undefined;

  const advisoryFindings: AdvisoryFindingsNode[] = (
    findingsUnion?.edges || []
  ).flatMap((e) => (e?.node?.__typename === "AdvisoryFinding" ? e.node : []));

  const hasAdvisoryFindings = advisoryFindings.length > 0;

  return (
    <Fragment>
      <ScrollToTop />
      <SectionHeading heading="Related Advisories" />
      {loading || !data || !finding ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <Loading />
        </Box>
      ) : (
        <>
          {hasAdvisoryFindings ? (
            <FindingsTable
              findings={advisoryFindings}
              totalCount={findingsUnion?.totalCount || 0}
              context={"advisory"}
              assetId={finding.asset?.id}
              canSelect={false}
              entityMrn={finding?.asset?.mrn || ""}
            />
          ) : (
            <EmptySection text="This CVE currently has no related advisories." />
          )}
        </>
      )}
    </Fragment>
  );
}
